import { create } from "zustand";

/**
 * Scope Exchange Store (v3)
 * 
 * Manages scope exchange payloads, integrity scores, latency,
 * and reconciliation delays between counterparties.
 */
export interface ScopeExchangeStoreState {
  outgoingPayloadKB: number;
  incomingPayloadKB: number;
  scopeCategory: "scope1" | "scope2" | "scope3";
  exchangeCounterpart: string;
  integrityScore: number; // 0-100
  latencyMS: number;
  reconciliationDelayDays: number;
  downstreamPropCoeff: number;

  // Actions
  setOutgoingPayloadKB: (kb: number) => void;
  setIncomingPayloadKB: (kb: number) => void;
  setScopeCategory: (category: "scope1" | "scope2" | "scope3") => void;
  setExchangeCounterpart: (counterpart: string) => void;
  setIntegrityScore: (score: number) => void;
  setLatencyMS: (ms: number) => void;
  setReconciliationDelayDays: (days: number) => void;
  setDownstreamPropCoeff: (coeff: number) => void;

  // Derived outputs
  getRiskPropagation: () => number;
  getLossOfIntegrity: () => number;
  getExchangeHealth: () => number;

  // Export
  exportScopeExchange: () => void;
}

export const useScopeExchangeStore = create<ScopeExchangeStoreState>((set, get) => ({
  outgoingPayloadKB: 0,
  incomingPayloadKB: 0,
  scopeCategory: "scope1",
  exchangeCounterpart: "",
  integrityScore: 100,
  latencyMS: 0,
  reconciliationDelayDays: 0,
  downstreamPropCoeff: 1.0,

  setOutgoingPayloadKB: (kb) => set({ outgoingPayloadKB: Math.max(0, kb) }),
  setIncomingPayloadKB: (kb) => set({ incomingPayloadKB: Math.max(0, kb) }),
  setScopeCategory: (category) => set({ scopeCategory: category }),
  setExchangeCounterpart: (counterpart) => set({ exchangeCounterpart: counterpart }),
  setIntegrityScore: (score) => set({ integrityScore: Math.max(0, Math.min(100, score)) }),
  setLatencyMS: (ms) => set({ latencyMS: Math.max(0, ms) }),
  setReconciliationDelayDays: (days) => set({ reconciliationDelayDays: Math.max(0, days) }),
  setDownstreamPropCoeff: (coeff) => set({ downstreamPropCoeff: Math.max(0, coeff) }),

  getRiskPropagation: () => {
    const state = get();
    return state.downstreamPropCoeff * (state.incomingPayloadKB / 10);
  },

  getLossOfIntegrity: () => {
    const state = get();
    return (100 - state.integrityScore) * 0.02;
  },

  getExchangeHealth: () => {
    const state = get();
    const riskProp = state.getRiskPropagation();
    const lossInteg = state.getLossOfIntegrity();
    const health = 100 - (riskProp + lossInteg + state.reconciliationDelayDays * 0.5);
    return Math.max(0, Math.min(100, health));
  },

  exportScopeExchange: () => {
    const state = get();
    const report = {
      outgoingPayloadKB: state.outgoingPayloadKB,
      incomingPayloadKB: state.incomingPayloadKB,
      scopeCategory: state.scopeCategory,
      exchangeCounterpart: state.exchangeCounterpart,
      integrityScore: state.integrityScore,
      latencyMS: state.latencyMS,
      reconciliationDelayDays: state.reconciliationDelayDays,
      downstreamPropCoeff: state.downstreamPropCoeff,
      riskPropagation: state.getRiskPropagation(),
      lossOfIntegrity: state.getLossOfIntegrity(),
      exchangeHealth: state.getExchangeHealth(),
    };

    const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scopeExchange_${dateStr}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },
}));











